import { ConnectionPositionPair, Overlay, OverlayRef } from '@angular/cdk/overlay';

import { ComponentPortal } from '@angular/cdk/portal';
import {
  Directive, ElementRef, HostListener, OnInit
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

import { TimePickerComponent } from '../time-picker.component';

/**
 * This directive connects a `TimePicker` component to a source input element.
 * This causes click events to the source input element to trigger the time picker to open in a nearby popup.
 * Furthermore this also establishes two-way data binding between the opened time picker and the source input element;
 * i.e. time values input into the time picker will update the value of the input element and vice versa.
 *
 * (*Note: it assumes that the source input element is tied to a FormControl in a reactive form.*)
 */
@Directive({
  selector: '[timePicker]',
  standalone: false
})
export class TimePickerDirective implements OnInit {
  private timePickerOverlay: OverlayRef;
  private eventBindingSub: Subscription;

  /**
   * Initializes `timePickerOverlay` which is the overlay reference that hosts the time picker.
   * We configure it with a `FlexibleConnectedPositionStrategy` position strategy
   * so that it will be positioned relative to the input host element.
   */
  private initializeOverlay(): void {
    const positionRelativeToHostInputStrategy = this.overlayService.position().flexibleConnectedTo(this.hostElementRef)
      .withPositions(
        [new ConnectionPositionPair(
          { originX: 'start', originY: 'bottom' },
          { overlayX: 'start', overlayY: 'top' }
        )]
      );

    this.timePickerOverlay = this.overlayService.create({
      positionStrategy: positionRelativeToHostInputStrategy
    });
  }

  private openTimePickerOverlay(): void {
    if (!this.timePickerOverlay.hasAttached()) {
      // create a new time picker component portal and inject it into the overlay
      const timePickerPortal = new ComponentPortal(TimePickerComponent);
      const timePickerComponent = this.timePickerOverlay.attach(timePickerPortal);

      /* bind the input time for the newly created time picker component instance
      to whatever time value currently exists in the host input element */
      timePickerComponent.instance.time = this.hostFormControl.value;
      timePickerComponent.changeDetectorRef.detectChanges();

      /* bind the host input element to update whenever the time value changes
      within the time picker component */
      this.eventBindingSub = timePickerComponent.instance.timeChange.subscribe(
        (outputTime) => {
          this.hostFormControl.control.setValue(outputTime);
          this.hostFormControl.control.markAsDirty();
        }
      );

      // close the timepicker overlay whenever the timepicker loses focus
      timePickerComponent.instance.onBlur.pipe(take(1)).subscribe(
        (_) => this.closeTimePickerOverlay()
      );
    }
  }

  private closeTimePickerOverlay(): void {
    if (this.timePickerOverlay.hasAttached()) {
      this.timePickerOverlay.detach();
    }

    if (this.eventBindingSub) {
      this.eventBindingSub.unsubscribe();
    }
  }

  @HostListener('focus') onInputFocus(): void {
    this.openTimePickerOverlay();
  }

  ngOnInit() {
    this.initializeOverlay();
  }

  constructor(
    private hostElementRef: ElementRef,
    private hostFormControl: NgControl,
    private overlayService: Overlay
  ) { }
}
